/**
 * Checkout shipping cost recalculation.
 */
import { useCallback, useRef } from "react";
import type { CheckoutState } from "../components/shop/Checkout/types";
import type { Dispatch, SetStateAction } from "react";
import type { PublicViewContextValue } from "./useCheckoutState";
import { isPODShop } from "../components/shop/helpers/helpers";
import type { Shop } from "../interfaces/Shop";
import { toast } from "react-toastify";
import { oauth } from "../utils";
import { resolveCartLineProductSkuId } from "./useCheckoutOrder";

interface UseCheckoutShippingArgs {
  setState: Dispatch<SetStateAction<CheckoutState>>;
  context: PublicViewContextValue | undefined;
  getState: () => CheckoutState;
}

function buildPodShippingProductSkuLines(cart: CheckoutState["cart"]) {
  const lines: Array<{ product_sku_id: string; quantity: number }> = [];

  for (const item of cart?.items || []) {
    for (const breakdown of item.breakdowns || []) {
      const quantity = Number(breakdown.quantity);
      if (!(quantity > 0)) {
        continue;
      }
      const productSkuId = resolveCartLineProductSkuId(
        item,
        breakdown as Parameters<typeof resolveCartLineProductSkuId>[1],
      );
      if (!productSkuId) {
        continue;
      }
      lines.push({ product_sku_id: productSkuId, quantity });
    }
  }

  return lines;
}

function applyShippingRange(
  setState: Dispatch<SetStateAction<CheckoutState>>,
  shippingRanges:
    | Record<
        string,
        {
          min: number;
          max?: number;
          value: number;
          shipping_range_id: string;
        }
      >
    | undefined,
) {
  setState((s) => {
    const orderTotal = s.orderTotal;
    const rangesArr = Object.values(shippingRanges ?? {});
    const selected = rangesArr.filter(
      (r: {
        min: number;
        max?: number;
        value: number;
        shipping_range_id: string;
      }) =>
        (orderTotal >= r.min && orderTotal < (r.max ?? Infinity)) ||
        (orderTotal >= r.min && !r.max),
    );
    if (selected.length === 1) {
      const next = selected[0];
      if (
        s.shipping_cost === next.value &&
        s.shipping_range_id === next.shipping_range_id
      ) {
        return s;
      }
      return {
        ...s,
        shipping_cost: next.value,
        shipping_range_id: next.shipping_range_id,
      };
    }
    return s;
  });
}

export function useCheckoutShipping({
  setState,
  context,
  getState,
}: UseCheckoutShippingArgs) {
  const contextRef = useRef(context);
  contextRef.current = context;
  const getStateRef = useRef(getState);
  getStateRef.current = getState;
  const podShippingRequestIdRef = useRef(0);

  const handleRecalculateShippingCost = useCallback(() => {
    const ctx = contextRef.current;
    const entity = ctx?.publicViewEntity?.entity as unknown as
      | (Shop & {
          shipping_ranges?: Record<
            string,
            {
              min: number;
              max?: number;
              value: number;
              shipping_range_id: string;
            }
          >;
        })
      | undefined;
    const entityBuyInventory = ctx?.publicViewEntity?.buyInventory;
    if (entityBuyInventory) return;

    const usePodAutoShipping =
      !!entity &&
      isPODShop(entity) &&
      +entity.calculate_pod_shipping_automatically === 1;

    if (!usePodAutoShipping) {
      applyShippingRange(setState, entity?.shipping_ranges);
      return;
    }

    const shopId =
      (entity.shop_id as string | undefined) ||
      ctx?.publicViewEntity?.entityId;
    if (!shopId) {
      return;
    }

    const lines = buildPodShippingProductSkuLines(getStateRef.current().cart);
    if (!lines.length) {
      podShippingRequestIdRef.current += 1;
      setState((s) => ({
        ...s,
        shipping_cost: 0,
        shipping_range_id: null,
      }));
      return;
    }

    const requestId = ++podShippingRequestIdRef.current;
    void oauth(
      "POST",
      "shop",
      {
        action: "calculate-pod-shipping",
        shop_id: shopId,
        lines,
      },
      undefined,
      { logout: false },
    )
      .then(({ json }: { json: { shipping_cost?: number | null; error?: string } }) => {
        if (requestId !== podShippingRequestIdRef.current) {
          return;
        }
        if (
          json?.error ||
          null === json?.shipping_cost ||
          undefined === json?.shipping_cost
        ) {
          console.error(
            "PoD shipping calculate failed",
            json?.error ?? "missing shipping_cost",
          );
          toast.error(
            json?.error ||
              "We couldn't calculate shipping right now. Please try again.",
          );
          return;
        }
        setState((s) => ({
          ...s,
          shipping_cost: Number(json.shipping_cost),
          shipping_range_id: null,
        }));
      })
      .catch((err: unknown) => {
        if (requestId !== podShippingRequestIdRef.current) {
          return;
        }
        console.error("PoD shipping calculate failed", err);
        toast.error(
          "We couldn't calculate shipping right now. Please try again.",
        );
      });
  }, [setState]);

  return { handleRecalculateShippingCost };
}
